import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Download, Book, Clock, Users, Heart, Quote } from 'lucide-react';
import Navbar from '../../components/Navbar/Navbar';
import Footer from '../../components/Footer/Footer';
import StickyBottomNav from '../../components/Navbar/StickyBottomNav';
import { Document, Page } from 'react-pdf';
import { pdfjs } from 'react-pdf';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';

// Add this line after imports
pdfjs.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.mjs`;

const SFRReflections = () => {

  
  // Mock book data
  const bookData = {
    id: 1,
    title: "SFR Reflections: How Serving Refugees has Changed our Lives",
    subtitle: "June 2025, SFR Homeschooling Branch",
    totalPages: 10,
    description: "A collection of personal stories from students who have had the profound opportunity to meet, support, and learn from refugees in their communities. Their reflections show how serving others can redraw the map of a life sometimes in bold lines, sometimes in quiet strokes, always with purpose.",
    stories: [
      {
        title: "A Glimmer of Hope Amidst a Sea of Challenges",
        author: "Aisha Muqsit",
        pages: "3-4",
        excerpt: "Meeting Mohammed reminded me that helping others doesn't always mean doing something extraordinary. In many circumstances, it's about being present for someone, listening to them, and supporting them..."
      },
      {
        title: "The Silent Strength of Gratitude", 
        author: "Aleeza Mian",
        pages: "5",
        excerpt: "They were content with what they had, a lesson I myself needed to learn. Sometimes, I would close my eyes and think about the experiences they must have faced.Was their home raided? Did they lose someone close to them..."
      },
      {
        title: "The Special Present (Presence)",
        author: "Hasbi Faruqui", 
        pages: "6-7",
        excerpt: "What I learned that afternoon, sitting with Mama Aisha, is that hospitality is a function of the size of a person's heart, not how big a house they have or how many possessions they own..."
      },
      {
        title: "Sunday Lessons: Determination and Resilience",
        author: "Zahra & Zaynab Latheef",
        pages: "8-9",
        excerpt: "Whenever he came across a word he didn't know, he stopped, and broke down its syllables before putting it together. Watching his determination—his persistence despite the struggle—was a moment that stayed with both of us..."
      }
    ]
  };

  const publications = [bookData]; // Can add more publications later

  const [showPDFViewer, setShowPDFViewer] = useState(false);
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);

  // Add keyboard navigation
  React.useEffect(() => {
    if (showPDFViewer) {
      const handleKeyDown = (event) => {
        if (event.key === 'ArrowLeft') {
          setPageNumber(Math.max(1, pageNumber - 1));
        } else if (event.key === 'ArrowRight') {
          setPageNumber(Math.min(numPages || 1, pageNumber + 1));
        } else if (event.key === 'Escape') {
          setShowPDFViewer(false);
        }
      };

      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }
  }, [showPDFViewer, pageNumber, numPages]);

  return (
    <div className="min-h-screen bg-lightTeal">
      <Navbar />
      
      <div className="pt-6 sm:pt-8 md:pt-12 lg:pt-16 pb-16 sm:pb-20 md:pb-24 lg:pb-32 px-3 sm:px-4 md:px-6 lg:px-8 xl:px-12">
        <div className="max-w-7xl mx-auto">
          {/* Page Header */}
          <motion.div 
            className="text-center mb-8 sm:mb-10 md:mb-12 lg:mb-16"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="font-display text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl text-secondary mb-3 sm:mb-4 lg:mb-6 px-2">
              SFR PUBLICATIONS
            </h1>
            <p className="text-sm sm:text-base md:text-lg lg:text-xl text-textPrimary max-w-xs sm:max-w-md md:max-w-lg lg:max-w-2xl xl:max-w-3xl mx-auto mb-4 sm:mb-6 px-4">
              Discover our collection of stories, reflections, and insights from our community
            </p>
            <div className="w-12 sm:w-14 md:w-16 lg:w-20 h-0.5 sm:h-1 bg-cta mx-auto"></div>
          </motion.div>

          {/* Publications Grid */}
          <div className="grid grid-cols-1 gap-6 sm:gap-8 lg:gap-12">
            {publications.map((publication, index) => (
              <motion.div
                key={publication.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                {/* Hero Section for Publication */}
                <div className="relative bg-gradient-to-br from-secondary via-primary to-primary rounded-2xl sm:rounded-3xl overflow-hidden text-white">
                  <div className="absolute inset-0 bg-black/20"></div>
                  <div className="relative z-10 p-4 sm:p-6 md:p-8 lg:p-12 xl:p-16">
                    <div className="max-w-4xl">
                      <div className="flex items-center mb-3 sm:mb-4 lg:mb-6">
                        <Book className="w-5 sm:w-6 md:w-7 lg:w-8 h-5 sm:h-6 md:h-7 lg:h-8 mr-2 sm:mr-3" />
                        <span className="text-lightTeal font-body font-medium text-xs sm:text-sm md:text-base">
                          New Publication
                        </span>
                      </div>
                      <h2 className="font-display text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-bold mb-3 sm:mb-4 md:mb-6 leading-tight">
                        SFR Reflections
                      </h2>
                      <p className="font-body text-sm sm:text-base md:text-lg lg:text-xl xl:text-2xl text-white mb-4 sm:mb-6 md:mb-8 leading-relaxed">
                        How Serving Refugees has Changed our Lives
                      </p>
                      <div className="flex flex-wrap gap-3 sm:gap-4 md:gap-6 text-lightTeal mb-4 sm:mb-6 md:mb-8 font-body text-xs sm:text-sm md:text-base">
                        <div className="flex items-center">
                          <Clock className="w-3 sm:w-4 md:w-5 h-3 sm:h-4 md:h-5 mr-1 sm:mr-2" />
                          June 2025
                        </div>
                        <div className="flex items-center">
                          <Users className="w-3 sm:w-4 md:w-5 h-3 sm:h-4 md:h-5 mr-1 sm:mr-2" />
                          SFR Homeschooling Branch
                        </div>
                        <div className="flex items-center">
                          <Heart className="w-3 sm:w-4 md:w-5 h-3 sm:h-4 md:h-5 mr-1 sm:mr-2" />
                          {publication.stories.length} Stories
                        </div>
                      </div>
                      <div className="flex flex-col sm:flex-row flex-wrap gap-3 sm:gap-4">
                        <button 
                          onClick={() => setShowPDFViewer(true)}
                          className="bg-white text-primary px-4 sm:px-6 md:px-8 py-2 sm:py-3 rounded-full font-body font-semibold hover:bg-lightTeal transition-colors text-sm sm:text-base text-center"
                        >
                          Read Online
                        </button>
                        <a 
                          href="/documents/SFR-Reflections.pdf" 
                          download="SFR-Reflections-How-Serving-Refugees-Changed-Our-Lives.pdf"
                          className="border-2 border-white text-white px-4 sm:px-6 md:px-8 py-2 sm:py-3 rounded-full font-body font-semibold hover:bg-white hover:text-primary transition-colors flex items-center justify-center text-sm sm:text-base"
                        >
                          <Download className="w-3 sm:w-4 md:w-5 h-3 sm:h-4 md:h-5 mr-1 sm:mr-2" />
                          Download PDF
                        </a>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Stories Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 md:gap-8 mt-6 sm:mt-8">
                  {publication.stories.map((story, storyIndex) => (
                    <motion.div 
                      key={storyIndex} 
                      className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 md:p-8 shadow-lg hover:shadow-xl transition-shadow"
                      whileHover={{ y: -5 }}
                    >
                      <div className="flex items-start justify-between mb-3 sm:mb-4">
                        <div className="bg-lightTeal text-primary px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-body font-medium">
                          Pages {story.pages}
                        </div>
                        <Quote className="w-4 sm:w-5 md:w-6 h-4 sm:h-5 md:h-6 text-tertiary flex-shrink-0" />
                      </div>
                      <h3 className="font-display text-base sm:text-lg md:text-xl font-bold text-secondary mb-2 sm:mb-3 leading-tight">
                        {story.title}
                      </h3>
                      <p className="text-tertiary font-body font-medium mb-3 sm:mb-4 text-sm sm:text-base">
                        by {story.author}
                      </p>
                      <p className="text-textPrimary font-body leading-relaxed text-xs sm:text-sm md:text-base">
                        {story.excerpt}
                      </p>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>

          {/* PDF Viewer Modal */}
          {showPDFViewer && (
            <div className="fixed inset-0 bg-white z-50 flex flex-col">
              <div className="bg-white w-full h-full overflow-hidden flex flex-col">
                {/* Header */}
                <div className="bg-primary text-white p-3 sm:p-4 md:p-6 flex items-center justify-between flex-shrink-0">
                  <h3 className="font-display text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold truncate pr-4">
                    SFR Reflections
                  </h3>
                  <button 
                    onClick={() => setShowPDFViewer(false)}
                    className="text-white hover:bg-white hover:bg-opacity-20 p-2 sm:p-3 rounded-full text-xl sm:text-2xl leading-none flex-shrink-0 w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 flex items-center justify-center"
                  >
                    ✕
                  </button>
                </div>
                
                {/* Controls */}
                <div className="border-b border-lightTeal p-3 sm:p-4 md:p-6 flex items-center justify-between flex-shrink-0">
                  <div className="flex items-center space-x-1 sm:space-x-2 md:space-x-4">
                    <button 
                      onClick={() => setPageNumber(Math.max(1, pageNumber - 1))}
                      disabled={pageNumber <= 1}
                      className="px-2 py-1 sm:px-3 sm:py-2 md:px-4 md:py-2 bg-primary text-white rounded disabled:bg-gray-300 text-xs sm:text-sm md:text-base font-medium"
                    >
                      Previous
                    </button>
                    <span className="text-xs sm:text-sm md:text-base font-medium text-secondary whitespace-nowrap">
                      Page {pageNumber} of {numPages || '?'}
                    </span>
                    <button 
                      onClick={() => setPageNumber(Math.min(numPages || 1, pageNumber + 1))}
                      disabled={pageNumber >= (numPages || 1)}
                      className="px-2 py-1 sm:px-3 sm:py-2 md:px-4 md:py-2 bg-primary text-white rounded disabled:bg-gray-300 text-xs sm:text-sm md:text-base font-medium"
                    >
                      Next
                    </button>
                  </div>
                  <a 
                    href="/documents/SFR-Reflections.pdf"
                    download="SFR-Reflections.pdf"
                    className="px-2 py-1 sm:px-3 sm:py-2 md:px-4 md:py-2 bg-cta text-white rounded text-xs sm:text-sm md:text-base hover:bg-opacity-90 transition-colors font-medium whitespace-nowrap"
                  >
                    Download
                  </a>
                </div>
                
                {/* PDF Display */}
                <div className="flex-1 overflow-auto bg-lightTeal p-1 sm:p-2 md:p-4 flex justify-center items-start">
                  <Document
                    file="/documents/SFR-Reflections.pdf"
                    onLoadSuccess={({ numPages }) => setNumPages(numPages)}
                    loading={
                      <div className="text-center p-4 sm:p-6 md:p-8 text-textPrimary">
                        <div className="animate-spin rounded-full h-8 w-8 sm:h-12 sm:w-12 border-b-2 border-primary mx-auto mb-4"></div>
                        <p className="text-sm sm:text-base">Loading PDF...</p>
                      </div>
                    }
                  >
                    <Page 
                      pageNumber={pageNumber} 
                      width={Math.min(window.innerWidth * (window.innerWidth < 640 ? 0.98 : window.innerWidth < 1024 ? 0.95 : 0.90), 800)}
                      renderTextLayer={false}
                      renderAnnotationLayer={false}
                      className="shadow-md sm:shadow-lg rounded"
                    />
                  </Document>
                </div>
              </div>
            </div>
          )}

          {/* Additional Publications Placeholder */}
          <motion.div 
            className="mt-12 sm:mt-16 lg:mt-20 text-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.8 }}
          >
            <div className="bg-white rounded-xl sm:rounded-2xl p-6 sm:p-8 md:p-10 shadow-md max-w-xs sm:max-w-sm md:max-w-md mx-auto">
              <Book className="w-12 sm:w-14 md:w-16 h-12 sm:h-14 md:h-16 text-primary mx-auto mb-3 sm:mb-4" />
              <h3 className="font-display text-lg sm:text-xl md:text-2xl lg:text-3xl text-secondary mb-2 sm:mb-3">
                More Coming Soon
              </h3>
              <p className="text-textPrimary font-body text-sm sm:text-base md:text-lg leading-relaxed">
                We're working on additional publications that will be available here soon.
              </p>
            </div>
          </motion.div>
        </div>
      </div>

      <Footer />
      <StickyBottomNav />
    </div>
  );
};

export default SFRReflections;